import React from "react";
import { cn } from "@/lib/utils/cn";
import { motion } from "motion/react";

interface StandardGradientHeaderProps {
  children: React.ReactNode;
  className?: string;
  variant?: "blue" | "dark";
}

/**
 * A reusable modern gradient header component used across details dialogs.
 * 
 * @param children - The content to display inside the header
 * @param className - Additional classes for the container
 * @param variant - The color theme of the gradient (defaults to blue)
 */
export const StandardGradientHeader = ({ 
  children, 
  className,
  variant = "blue"
}: StandardGradientHeaderProps) => {
  const variants = {
    blue: "from-blue-600 to-blue-800",
    dark: "from-gray-800 to-gray-950"
  };

  const decorativeColors = {
    blue: {
      top: "bg-white/10",
      bottom: "bg-blue-400/20"
    },
    dark: {
      top: "bg-white/5",
      bottom: "bg-white/5"
    }
  };

  return (
    <div className={cn(
      "relative overflow-hidden rounded-3xl p-8 shadow-lg bg-gradient-to-br", 
      variants[variant],
      className
    )}>
      {/* Decorative background elements */}
      <div className={cn(
        "absolute top-0 right-0 w-32 h-32 rounded-full -mr-16 -mt-16 blur-2xl",
        decorativeColors[variant].top
      )} />
      <div className={cn(
        "absolute bottom-0 left-0 w-24 h-24 rounded-full -ml-12 -mb-12 blur-xl",
        decorativeColors[variant].bottom
      )} />
      
      {/* Light Sweep Shine Animation */}
      <motion.div
        className="absolute inset-0 z-20 pointer-events-none"
        initial={{ x: "-100%", opacity: 0 }}
        animate={{ x: "200%", opacity: 1 }}
        transition={{ 
          duration: 2, 
          ease: "easeOut",
          delay: 0
        }}
      >
        <div className="w-1/2 h-full bg-gradient-to-r from-transparent via-white/25 to-transparent -skew-x-[30deg] transform" />
      </motion.div>

      <div className="relative z-10">
        {children}
      </div>
    </div>
  );
};
